/**
 * Timezone helpers without a library: every instant is rendered in the LOCATION's zone
 * (FRONTEND_PLAN §6.8), and staff-entered wall-clock times are converted back to instants in
 * that zone — never the browser's.
 */
import type { Instant } from '@/api/types'

const partsFormatter = new Map<string, Intl.DateTimeFormat>()

function formatter(timeZone: string): Intl.DateTimeFormat {
  let cached = partsFormatter.get(timeZone)
  if (cached === undefined) {
    cached = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
    partsFormatter.set(timeZone, cached)
  }
  return cached
}

interface WallClock {
  readonly year: number
  readonly month: number
  readonly day: number
  readonly hour: number
  readonly minute: number
  readonly second: number
}

/** The wall-clock reading of `date` in `timeZone`. */
export function wallClockIn(date: Date, timeZone: string): WallClock {
  const parts = formatter(timeZone).formatToParts(date)
  const get = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((p) => p.type === type)?.value ?? '0')
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour') % 24,
    minute: get('minute'),
    second: get('second'),
  }
}

/** Offset of `timeZone` from UTC at `date`, in milliseconds (positive east of UTC). */
export function offsetMs(date: Date, timeZone: string): number {
  const w = wallClockIn(date, timeZone)
  const asUtc = Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second)
  return asUtc - Math.floor(date.getTime() / 1000) * 1000
}

/**
 * `"YYYY-MM-DDTHH:MM"` (an <input type="datetime-local"> value) read as wall-clock time in
 * `timeZone` → ISO instant. Two passes handle DST transitions.
 */
export function zonedLocalToInstant(local: string, timeZone: string): Instant {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(local)
  if (match === null) throw new TypeError(`Not a local date-time: ${local}`)
  const [, y, mo, d, h, mi, s = '0'] = match
  const guess = Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s))
  let instant = guess - offsetMs(new Date(guess), timeZone)
  instant = guess - offsetMs(new Date(instant), timeZone)
  return new Date(instant).toISOString()
}

/** ISO instant → `"YYYY-MM-DDTHH:MM"` in `timeZone`, for a datetime-local input. */
export function instantToZonedLocal(instant: Instant, timeZone: string): string {
  const w = wallClockIn(new Date(instant), timeZone)
  const pad = (n: number): string => String(n).padStart(2, '0')
  return `${String(w.year)}-${pad(w.month)}-${pad(w.day)}T${pad(w.hour)}:${pad(w.minute)}`
}

export interface FormatInZoneOptions {
  readonly dateStyle?: 'short' | 'medium' | 'long' | 'full'
  readonly timeStyle?: 'short' | 'medium'
  readonly locale?: string
}

/** Human date/time in `timeZone`, e.g. "Sep 12, 2026, 2:00 PM". */
export function formatInZone(
  instant: Instant | Date,
  timeZone: string,
  options: FormatInZoneOptions = {},
): string {
  const date = typeof instant === 'string' ? new Date(instant) : instant
  return new Intl.DateTimeFormat(options.locale ?? 'en-US', {
    timeZone,
    dateStyle: options.dateStyle ?? 'medium',
    timeStyle: options.timeStyle ?? 'short',
  }).format(date)
}

/** Time only in `timeZone`, e.g. "2:00 PM". */
export function formatTimeInZone(
  instant: Instant | Date,
  timeZone: string,
  locale = 'en-US',
): string {
  const date = typeof instant === 'string' ? new Date(instant) : instant
  return new Intl.DateTimeFormat(locale, { timeZone, timeStyle: 'short' }).format(date)
}

/** Short zone label such as "EDT", for disambiguating when the viewer is elsewhere. */
export function zoneAbbreviation(instant: Instant | Date, timeZone: string): string {
  const date = typeof instant === 'string' ? new Date(instant) : instant
  const part = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' })
    .formatToParts(date)
    .find((p) => p.type === 'timeZoneName')
  return part?.value ?? timeZone
}

/** All IANA zones the runtime knows, for a picker. */
export function supportedTimeZones(): readonly string[] {
  try {
    return Intl.supportedValuesOf('timeZone')
  } catch {
    return [
      'UTC',
      'America/New_York',
      'America/Chicago',
      'America/Denver',
      'America/Los_Angeles',
      'Europe/London',
    ]
  }
}
